import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { streamChat } from "@/lib/stream";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles } from "lucide-react";

const QUICK = [
  "I'm sore today, adjust my workout",
  "Suggest a substitute for pull-ups",
  "How do I improve my squat?",
  "Build me a quick 20-min session",
];

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/ai/history/coach").then(({ data }) => {
      setMessages(data.map((m) => ({ role: m.role, text: m.text })));
    }).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      await streamChat("/ai/coach/stream", msg, (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry, I hit an error. Try again." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <AppShell title="AI Coach" hideNav>
      <div className="flex flex-col" style={{ height: "calc(100vh - 130px)" }}>
        <div data-testid="ai-coach-message-list" className="flex-1 overflow-y-auto space-y-3 pb-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="h-12 w-12 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="h-6 w-6 text-[#ccff0c]" />
              </div>
              <p className="text-muted-foreground">Your coach is ready. Ask anything.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#ccff0c] text-black rounded-2xl rounded-br-md" : "bg-secondary text-foreground border border-border rounded-2xl rounded-bl-md"}`}>
                {m.text || (busy && i === messages.length - 1 ? <span className="rd-typing"><span></span><span></span><span></span></span> : "")}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {messages.length === 0 && (
          <div className="flex gap-2 overflow-x-auto rd-scrollbar pb-2">
            {QUICK.map((q) => (
              <button key={q} onClick={() => send(q)} className="whitespace-nowrap rounded-full px-3 py-1.5 text-xs bg-card border border-border text-muted-foreground">{q}</button>
            ))}
          </div>
        )}

        <div data-testid="ai-coach-composer" className="flex items-end gap-2 pt-2 pb-4 border-t border-border">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask your coach…"
            className="bg-card border-border resize-none min-h-[44px] max-h-32"
            rows={1}
          />
          <Button data-testid="ai-coach-send-button" onClick={() => send()} disabled={busy || !input.trim()} className="h-11 w-11 p-0 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0">
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
